import { c as _c } from "react/compiler-runtime";
import type { CommentListInput } from '@/orpc/client';
import NumberFlow, { NumberFlowGroup } from '@number-flow/react';
import { ListFilterIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useCommentsContext } from '@/contexts/comments.context';
import { useCountComment } from '@/hooks/queries/comment.query';
import { useCountReply } from '@/hooks/queries/reply.query';
export function CommentHeader() {
  const $ = _c(57);
  const {
    slug,
    sort,
    setSort
  } = useCommentsContext();
  const t = useTranslations();
  let t0;
  if ($[0] !== slug) {
    t0 = {
      slug
    };
    $[0] = slug;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const commentCountQuery = useCountComment(t0);
  let t1;
  if ($[2] !== slug) {
    t1 = {
      slug
    };
    $[2] = slug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const replyCountQuery = useCountReply(t1);
  let t2;
  if ($[4] !== commentCountQuery.isLoading || $[5] !== t) {
    t2 = commentCountQuery.isLoading && `-- ${t("blog.comments.comments", {
      count: 0
    })}`;
    $[4] = commentCountQuery.isLoading;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== commentCountQuery.isError || $[8] !== t) {
    t3 = commentCountQuery.isError && t("common.error");
    $[7] = commentCountQuery.isError;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== commentCountQuery.data || $[11] !== commentCountQuery.isSuccess || $[12] !== t) {
    t4 = commentCountQuery.isSuccess && <NumberFlow value={commentCountQuery.data.count} suffix={` ${t("blog.comments.comments", {
      count: commentCountQuery.data.count
    })}`} data-testid="blog-comment-count" />;
    $[10] = commentCountQuery.data;
    $[11] = commentCountQuery.isSuccess;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== replyCountQuery.isLoading || $[15] !== t) {
    t5 = replyCountQuery.isLoading && `-- ${t("blog.comments.replies", {
      count: 0
    })}`;
    $[14] = replyCountQuery.isLoading;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== replyCountQuery.isError || $[18] !== t) {
    t6 = replyCountQuery.isError && t("common.error");
    $[17] = replyCountQuery.isError;
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== replyCountQuery.data || $[21] !== replyCountQuery.isSuccess || $[22] !== t) {
    t7 = replyCountQuery.isSuccess && <NumberFlow value={replyCountQuery.data.count} suffix={` ${t("blog.comments.replies", {
      count: replyCountQuery.data.count
    })}`} data-testid="reply-count" />;
    $[20] = replyCountQuery.data;
    $[21] = replyCountQuery.isSuccess;
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t2 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7) {
    t8 = <NumberFlowGroup><div>{t2}{t3}{t4}{" \xB7 "}{t5}{t6}{t7}</div></NumberFlowGroup>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ListFilterIcon data-icon="inline-start" />;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t) {
    t10 = t("blog.comments.sort-by");
    $[32] = t;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10) {
    t11 = <DropdownMenuTrigger render={<Button variant="outline" size="sm">{t9}<span>{t10}</span></Button>} />;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== setSort) {
    t12 = value => {
      setSort(value as CommentListInput['sort']);
    };
    $[36] = setSort;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t) {
    t13 = t("blog.comments.newest");
    $[38] = t;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13) {
    t14 = <DropdownMenuRadioItem value="newest">{t13}</DropdownMenuRadioItem>;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t) {
    t15 = t("blog.comments.oldest");
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t15) {
    t16 = <DropdownMenuRadioItem value="oldest">{t15}</DropdownMenuRadioItem>;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== sort || $[47] !== t12 || $[48] !== t14 || $[49] !== t16) {
    t17 = <DropdownMenuContent align="end"><DropdownMenuRadioGroup value={sort} onValueChange={t12}>{t14}{t16}</DropdownMenuRadioGroup></DropdownMenuContent>;
    $[46] = sort;
    $[47] = t12;
    $[48] = t14;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t11 || $[52] !== t17) {
    t18 = <DropdownMenu>{t11}{t17}</DropdownMenu>;
    $[51] = t11;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t18 || $[55] !== t8) {
    t19 = <div className="flex items-center justify-between px-1">{t8}{t18}</div>;
    $[54] = t18;
    $[55] = t8;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
}